import { Fragment } from 'react';

import { Spacer } from '@procurify/ui'

import { BudgetTrackerCard } from '@webapp/components/Budgets/BudgetTrackerCard'
import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { type BudgetFormat, useFeatureFlag } from '@webapp/hooks'
import { type BudgetIntervalType } from '@webapp/models/enums/BudgetInterval'
import { SpendTrackerStyled } from './styles/SpendTrackerStyled'
import {
  type DepartmentalSpendCategoryCardData,
  formatSpendStringToNumber,
  type IBudgetCategoryCardData,
} from './utils'
import { BudgetCategoryCardLoader } from '../BudgetCategoryCard/components/BudgetCategoryCardLoader'
import { SpendTrackerCategoryCard } from '../SpendTrackerCategoryCard'
import { SpendTrackerDepartmentCard } from '../SpendTrackerDepartmentCard'
import { SpendTrackerUncategorizedCard } from '../SpendTrackerUncategorizedCard'

export interface IProcessedBudgetData {
  name: string
  uuid: uuid
  budgetSpendDetails: BudgetFormat
  startDate: Nullable<Date>
  endDate: Nullable<Date>
  period: Omit<BudgetIntervalType, 'none'> | null
  isSourceOfTruth?: boolean
  attributes: {
    departments: string[]
    accountCodes: string[]
  }
}

export interface ISpendTrackerProps {
  isLoading?: boolean
  primaryOrganizationName?: string
  secondaryOrganizationName?: string
  departmentalSpend?: DepartmentalSpendCategoryCardData
  budgetCategories?: IBudgetCategoryCardData[]
  processedBudgetData?: IProcessedBudgetData[]
  uncategorizedSpend?: string
  timeZoneTooltip: TimezonesEnum
}

export const SpendTracker = ({
  isLoading = true,
  primaryOrganizationName,
  secondaryOrganizationName,
  departmentalSpend,
  budgetCategories,
  processedBudgetData,
  uncategorizedSpend,
  timeZoneTooltip,
}: ISpendTrackerProps) => {
  const FT_5817_ENABLE_BUDGETS_REDESIGN = useFeatureFlag(
    'FT_5817_ENABLE_BUDGETS_REDESIGN'
  )

  const currentUncategorizedSpend = uncategorizedSpend
    ? formatSpendStringToNumber(uncategorizedSpend)
    : null

  const renderDepartmentalSpendCard = () => {
    const {
      currentSpend,
      otherPendingRequests,
      spendAfterApproval,
      currentRequestSpend,
      committedSpend,
      requestedSpend,
      totalSpendBeforePending,
      pendingSpend,
      totalSpend,
      startDate,
      endDate,
    } = departmentalSpend

    return (
      <SpendTrackerDepartmentCard
        name={`${primaryOrganizationName} - ${secondaryOrganizationName}`}
        currentRequest={currentRequestSpend}
        currentSpend={currentSpend}
        otherPendingRequests={otherPendingRequests}
        spendAfterApproval={spendAfterApproval}
        startDate={startDate}
        endDate={endDate}
        timeZoneTooltip={timeZoneTooltip}
        committedSpend={committedSpend}
        requestedSpend={requestedSpend}
        totalSpendBeforePending={totalSpendBeforePending}
        pendingSpend={pendingSpend}
        totalSpend={totalSpend}
      />
    )
  }

  const renderBudgetCategoryCards = () => {
    if (FT_5817_ENABLE_BUDGETS_REDESIGN) {
      return processedBudgetData.map((processedBudget) => {
        const {
          budgetSpendDetails,
          startDate,
          endDate,
          period,
          attributes,
          name,
          uuid,
          isSourceOfTruth,
        } = processedBudget

        return (
          <Fragment key={uuid}>
            <BudgetTrackerCard
              name={name}
              startDate={startDate}
              endDate={endDate}
              isSourceOfTruth={isSourceOfTruth}
              departments={attributes?.departments}
              accountCodes={attributes?.accountCodes}
              timeZoneTooltip={timeZoneTooltip}
              period={period}
              budgetSpendDetails={budgetSpendDetails}
              showPercentagesOnlyForSummedTotals
            />
            <Spacer />
          </Fragment>
        );
      });
    }

    return budgetCategories.map((category) => {
      return (
        <Fragment key={category.uuid}>
          <SpendTrackerCategoryCard
            name={category.name}
            startDate={category.startDate}
            endDate={category.endDate}
            currentRequest={category.currentRequestSpend}
            otherPendingRequests={category.otherPendingRequests}
            spendAfterApproval={category.spendAfterApproval}
            currentSpend={category.currentSpend}
            departments={category.attributes?.departments}
            accountCodes={category.attributes?.accountCodes}
            budget={category?.budget}
            budgetBreakdown={category?.budgetBreakdown ?? []}
            timeZoneTooltip={timeZoneTooltip}
            period={category?.period}
            variance={category?.variance}
            variancePercentage={category?.variancePercentage}
            isOverBudget={category?.isOverBudget}
          />
          <Spacer />
        </Fragment>
      );
    });
  }

  const renderCardContent = () => {
    return departmentalSpend ? (
      renderDepartmentalSpendCard()
    ) : (
      <>
        {renderBudgetCategoryCards()}
        {!!currentUncategorizedSpend && !departmentalSpend && (
          <SpendTrackerUncategorizedCard
            currentSpend={currentUncategorizedSpend}
          />
        )}
      </>
    )
  }

  return (
    <SpendTrackerStyled>
      {isLoading ? <BudgetCategoryCardLoader /> : <>{renderCardContent()}</>}
    </SpendTrackerStyled>
  )
}
